import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  endCall as endCallRequest,
  handBack as handBackRequest,
  sendHeartbeat,
  startListening,
  startTakeover,
} from "../supervision-api";
import type { EngagementTier } from "../types";
import { preflightMicrophone, useCallAudio } from "./use-call-audio";

const DEFAULT_STALL = "Let me bring in a colleague who can help with that.";

/** Consecutive failures tolerated before we accept the hold is gone. */
const HEARTBEAT_FAILURE_LIMIT = 3;
const FALLBACK_HEARTBEAT_SECONDS = 20;

/**
 * What an action actually did. Failures are also kept in `error`; "skipped" means it was
 * ignored or superseded and nothing should be reported.
 */
export type SessionOutcome =
  | { status: "done" }
  | { status: "skipped" }
  | { status: "failed"; error: string };

const SKIPPED = "skipped" as const;

export interface AudioSession {
  /** How engaged we are with `callId`. "watch" means no room at all. */
  tier: EngagementTier;
  /** The call we are connected to, which need not be the one on screen. */
  callId: string | null;
  pendingCallId?: string | null;
  micLive: boolean;
  error: string | null;
  /** Set while a transition is in flight, so callers can disable their controls. */
  busy: "listen" | "takeover" | "handback" | "end" | null;
  listen: (callId: string) => Promise<SessionOutcome>;
  takeover: (callId: string) => Promise<SessionOutcome>;
  handBack: (briefing?: string) => Promise<SessionOutcome>;
  /** Ends `callId`, or the connected call. The drawer passes its own call so END never
   *  hangs up the call you happen to be connected to instead. */
  endCall: (callId?: string) => Promise<SessionOutcome>;
  setMicEnabled: (enabled: boolean) => Promise<void>;
  stop: () => Promise<void>;
  clearError: () => void;
}

const AudioSessionContext = createContext<AudioSession | null>(null);

/**
 * The supervisor's one and only audio connection, owned by the page.
 *
 * This lived inside the takeover controls, which are mounted inside the transcript pane,
 * which is keyed to the selected call -- so selecting a different call unmounted the hook
 * and its cleanup tore down the room. Listening to one call while reading another was
 * therefore impossible, which is most of what multi-call supervision *is*.
 *
 * Hoisting it here fixes that and makes the invariants statable in one place:
 *
 *  - exactly one LiveKit room exists, ever. A supervisor watching twenty-five calls must
 *    never be in twenty-five rooms;
 *  - a takeover pins the focus to its own call until hand-back or end-call;
 *  - the microphone is proven before the agent is ever asked to hold;
 *  - if anything fails *after* the hold is in, the call is handed straight back.
 */
export function AudioProvider({ children }: { children: ReactNode }) {
  const { tier, connectedCallId, micLive, error, connect, teardown, setMicEnabled, setError } =
    useCallAudio();

  const [pendingCallId, setPendingCallId] = useState<string | null>(null);
  const transitionRef = useRef(0);
  // The call a takeover is in flight for. A ref, not state: duplicate fires land before a re-render.
  const takeoverInFlightRef = useRef<string | null>(null);
  // The call the latest transition took over, if it was a takeover; outlives its completion.
  const latestTakeoverRef = useRef<string | null>(null);
  const [busy, setBusy] = useState<AudioSession["busy"]>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatSecondsRef = useRef<number>(FALLBACK_HEARTBEAT_SECONDS);
  const failuresRef = useRef(0);

  const holding = tier === "takeover" && connectedCallId !== null;

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = null;
    failuresRef.current = 0;
  }, []);

  useEffect(() => () => {
    ++transitionRef.current;
    stopHeartbeat();
  }, [stopHeartbeat]);

  const run = useCallback(
    async (
      kind: NonNullable<AudioSession["busy"]>,
      action: () => Promise<void | typeof SKIPPED>,
    ): Promise<SessionOutcome> => {
      setBusy(kind);
      setError(null);
      try {
        return { status: (await action()) === SKIPPED ? "skipped" : "done" };
      } catch (caught) {
        const message = (caught as Error)?.message || "That did not work.";
        setError(message);
        return { status: "failed", error: message };
      } finally {
        setBusy(null);
      }
    },
    [setError],
  );

  const listen = useCallback((callId: string) => {
    const transition = ++transitionRef.current;
    takeoverInFlightRef.current = null;
    latestTakeoverRef.current = null;
    setPendingCallId(callId);
    return run("listen", async () => {
      const grant = await startListening(callId);
      if (transition !== transitionRef.current) return SKIPPED;
      await connect(grant, callId);
    }).finally(() => {
      if (transition === transitionRef.current) setPendingCallId(null);
    });
  }, [run, connect]);

  const takeover = useCallback((callId: string): Promise<SessionOutcome> => {
    // QA's repeat presses each started a takeover; the stale one then handed the call back.
    if (takeoverInFlightRef.current === callId || (connectedCallId === callId && tier === "takeover")) {
      return Promise.resolve<SessionOutcome>({ status: "skipped" });
    }
    const transition = ++transitionRef.current;
    takeoverInFlightRef.current = callId;
    latestTakeoverRef.current = callId;
    setPendingCallId(callId);
    return run("takeover", async () => {
      try {
        await preflightMicrophone();
      } catch {
        throw new Error("Your microphone is not available, so the caller could not hear you. Nothing was changed on the call.");
      }
      if (transition !== transitionRef.current) return SKIPPED;
      const grant = await startTakeover(callId, {stall_text: DEFAULT_STALL});
      if (transition !== transitionRef.current) {
        // Not when a newer takeover is for this same call: that resumed the AI under it.
        if (latestTakeoverRef.current !== callId) {
          await handBackRequest(callId, "").catch(() => undefined);
        }
        return SKIPPED;
      }
      heartbeatSecondsRef.current = grant.heartbeat_seconds || FALLBACK_HEARTBEAT_SECONDS;
      try {
        await connect(grant, callId);
      } catch (caught) {
        await handBackRequest(callId, "").catch(() => undefined);
        throw caught;
      }
    }).finally(() => {
      if (transition !== transitionRef.current) return;
      setPendingCallId(null);
      takeoverInFlightRef.current = null;
    });
  }, [run, connect, connectedCallId, tier]);

  const handBack = useCallback(
    (briefing = "") => {
      const callId = connectedCallId;
      if (!callId) return Promise.resolve<SessionOutcome>({ status: "skipped" });
      return run("handback", async () => {
        const outcome = await handBackRequest(callId, briefing.trim());
        await teardown();
        // Core answers 200 even when the agent refused the resume; that is not a success.
        if (outcome?.resume?.accepted === false) {
          throw new Error(outcome.resume.detail || "The AI did not confirm it resumed the call.");
        }
      });
    },
    [run, connectedCallId, teardown],
  );

  const endCall = useCallback((target?: string) => {
    const callId = target ?? connectedCallId;
    if (!callId) return Promise.resolve<SessionOutcome>({ status: "skipped" });
    return run("end", async () => {
      const outcome = await endCallRequest(callId, "ended by supervisor");
      if (callId === connectedCallId) await teardown();
      if (outcome?.accepted === false) {
        throw new Error(outcome.detail || "The agent did not confirm the call ended.");
      }
    });
  }, [run, connectedCallId, teardown]);

  // While holding, keep saying "still here": one beat refreshes both the claim and the
  // agent's hold deadline. Stopping is the safety net -- the AI takes itself off hold
  // rather than leaving the call silent -- so a lost heartbeat is reported, never swallowed.
  useEffect(() => {
    stopHeartbeat();
    if (!holding || !connectedCallId) return;

    const callId = connectedCallId;
    const interval = setInterval(
      () => {
        void sendHeartbeat(callId)
          .then(() => {
            failuresRef.current = 0;
          })
          .catch(() => {
            failuresRef.current += 1;
            if (failuresRef.current < HEARTBEAT_FAILURE_LIMIT) return;
            // Close the mic so the supervisor can't talk over the AI, and hand back so the
            // claim and the agent's hold don't leave the caller in silence.
            void handBackRequest(callId, "").catch(() => undefined);
            void teardown();
            setError(
              "Lost your hold on this call — the AI has resumed and your microphone is off.",
            );
          });
      },
      heartbeatSecondsRef.current * 1000,
    );
    heartbeatRef.current = interval;
    return () => clearInterval(interval);
  }, [holding, connectedCallId, stopHeartbeat, teardown, setError]);

  const stop = useCallback(async () => {
    ++transitionRef.current;
    takeoverInFlightRef.current = null;
    latestTakeoverRef.current = null;
    setPendingCallId(null);
    stopHeartbeat();
    await teardown();
  }, [stopHeartbeat, teardown]);

  const value = useMemo<AudioSession>(
    () => ({
      tier,
      callId: connectedCallId,
      pendingCallId,
      micLive,
      error,
      busy,
      listen,
      takeover,
      handBack,
      endCall,
      setMicEnabled,
      stop,
      clearError: () => setError(null),
    }),
    [
      tier,
      connectedCallId,
      pendingCallId,
      micLive,
      error,
      busy,
      listen,
      takeover,
      handBack,
      endCall,
      setMicEnabled,
      stop,
      setError,
    ],
  );

  return <AudioSessionContext.Provider value={value}>{children}</AudioSessionContext.Provider>;
}

export function useAudioSession(): AudioSession {
  const ctx = useContext(AudioSessionContext);
  if (!ctx) throw new Error("useAudioSession must be used within an AudioProvider");
  return ctx;
}
